import { useEffect, useRef } from "react";

type ShortcutMap = Partial<Record<string, () => void>>;

/**
 * Registers page-level keyboard shortcuts.
 *
 * Combo syntax: "alt+n", "f2", "f5", "escape"
 * Alt/Ctrl combos fire even when an input is focused.
 * Bare function keys (F2, F5) are blocked when a plain text input has focus
 * to avoid clashing with native browser behaviour.
 *
 * @param shortcuts  Map of combo → handler
 * @param enabled    Set false when a modal/drawer is open over the page
 */
export function usePageKeyboardShortcuts(shortcuts: ShortcutMap, enabled = true) {
  const shortcutsRef = useRef(shortcuts);
  shortcutsRef.current = shortcuts;

  useEffect(() => {
    if (!enabled) return;

    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase() ?? "";
      const isPlainInput = ["input", "textarea"].includes(tag);

      const parts: string[] = [];
      if (e.altKey) parts.push("alt");
      if (e.ctrlKey) parts.push("ctrl");
      if (e.shiftKey) parts.push("shift");
      parts.push(e.key.toLowerCase());
      const combo = parts.join("+");

      const fn = shortcutsRef.current[combo];
      if (!fn) return;

      const hasModifier = e.altKey || e.ctrlKey;
      if (isPlainInput && !hasModifier) return;

      e.preventDefault();
      fn();
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled]);
}
